"use client";

import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Columns3,
  RotateCcw,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import type { TableSortState } from "./use-persistent-table-preferences";

export type ConfigurableDataTableColumn<TItem, TColumn extends string> = {
  id: TColumn;
  label: string;
  minWidth: number;
  render: (item: TItem) => ReactNode;
  sortable?: boolean;
  sortValue: (item: TItem) => string | number;
  width: number;
};

export type ConfigurableDataTableColumnGroup<TColumn extends string> = {
  columns: TColumn[];
  id: string;
  label: string;
};

type ConfigurableDataTableExtraColumn<TItem> = {
  cellClassName?: string;
  header: ReactNode;
  headerClassName?: string;
  key: string;
  position?: "end" | "start";
  render: (item: TItem) => ReactNode;
  width: number;
};

type ConfigurableDataTableCellRenderArgs<TItem, TColumn extends string> = {
  children: ReactNode;
  column: ConfigurableDataTableColumn<TItem, TColumn>;
  item: TItem;
};

export function ConfigurableDataTable<TItem, TColumn extends string>({
  cellClassName = "px-3 py-3 align-top text-slate-700 dark:text-slate-300",
  cellContentClassName = "line-clamp-3 break-words",
  columnOrder,
  columnGroups,
  columnWidths,
  columns,
  emptyState,
  extraColumns = [],
  fillWidth = false,
  getRowKey,
  headerClassName = "sticky top-0 z-10 bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900 dark:text-slate-400",
  items,
  moveColumn,
  onConfigurationOpenChange,
  onResetPreferencesRequest,
  resetPreferences,
  resetMenuLabel = "Restaurar tabela",
  onRowClick,
  onRowContextMenu,
  onRowDoubleClick,
  onRowKeyDown,
  onRowPointerDown,
  onRowPointerEnter,
  renderCellContent,
  resizeColumn,
  rowClassName,
  rowTabIndex,
  rowTitle,
  scrollClassName = "relative h-full min-h-0 min-w-0 overflow-auto",
  sort,
  sortItems,
  tableClassName = "table-fixed text-left text-sm",
  tableScrollAttribute,
  toggleColumnVisibility,
  toggleSort,
  visibleColumns,
}: {
  cellClassName?: string;
  cellContentClassName?: string;
  columnOrder: TColumn[];
  columnGroups?: Array<ConfigurableDataTableColumnGroup<TColumn>>;
  columnWidths: Record<TColumn, number>;
  columns: Array<ConfigurableDataTableColumn<TItem, TColumn>>;
  emptyState: ReactNode;
  extraColumns?: Array<ConfigurableDataTableExtraColumn<TItem>>;
  fillWidth?: boolean;
  getRowKey: (item: TItem) => string;
  headerClassName?: string;
  items: TItem[];
  moveColumn: (source: TColumn, target: TColumn) => void;
  onConfigurationOpenChange?: (open: boolean) => void;
  onResetPreferencesRequest?: () => void;
  resetPreferences?: () => void;
  resetMenuLabel?: string;
  onRowClick?: (item: TItem) => void | Promise<void>;
  onRowContextMenu?: (
    item: TItem,
    event: ReactMouseEvent<HTMLTableRowElement>,
  ) => void;
  onRowDoubleClick?: (
    item: TItem,
    event: ReactMouseEvent<HTMLTableRowElement>,
  ) => void | Promise<void>;
  onRowKeyDown?: (
    item: TItem,
    event: ReactKeyboardEvent<HTMLTableRowElement>,
  ) => void;
  onRowPointerDown?: (
    item: TItem,
    event: ReactPointerEvent<HTMLTableRowElement>,
  ) => void;
  onRowPointerEnter?: (
    item: TItem,
    event: ReactPointerEvent<HTMLTableRowElement>,
  ) => void;
  renderCellContent?: (
    args: ConfigurableDataTableCellRenderArgs<TItem, TColumn>,
  ) => ReactNode;
  resizeColumn: (column: TColumn, width: number) => void;
  rowClassName?: (item: TItem) => string;
  rowTabIndex?: number | ((item: TItem) => number);
  rowTitle?: (item: TItem) => string;
  scrollClassName?: string;
  sort: TableSortState<TColumn>;
  sortItems?: (
    items: TItem[],
    sort: TableSortState<TColumn>,
    columns: Array<ConfigurableDataTableColumn<TItem, TColumn>>,
  ) => TItem[];
  tableClassName?: string;
  tableScrollAttribute?: string;
  toggleColumnVisibility?: (column: TColumn) => void;
  toggleSort: (column: TColumn) => void;
  visibleColumns: TColumn[];
}) {
  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((columnId) => columns.find((column) => column.id === columnId))
        .filter(
          (column): column is ConfigurableDataTableColumn<TItem, TColumn> =>
            column !== undefined && visibleColumns.includes(column.id),
        ),
    [columnOrder, columns, visibleColumns],
  );
  const sortedItems = useMemo(
    () =>
      sortItems
        ? sortItems(items, sort, columns)
        : sortConfigurableDataTableItems(items, sort, columns),
    [columns, items, sort, sortItems],
  );
  const tableWidth = useMemo(
    () =>
      orderedColumns.reduce(
        (total, column) => total + columnWidths[column.id],
        extraColumns.reduce((total, column) => total + column.width, 0),
      ),
    [columnWidths, extraColumns, orderedColumns],
  );
  const leadingExtraColumns = extraColumns.filter(
    (column) => column.position === "start",
  );
  const trailingExtraColumns = extraColumns.filter(
    (column) => column.position !== "start",
  );
  const scrollAttribute = {
    "data-configurable-table-scroll": true,
    ...(tableScrollAttribute ? { [tableScrollAttribute]: true } : {}),
  };
  const [headerMenu, setHeaderMenu] = useState<{
    mode: "actions" | "columns";
    x: number;
    y: number;
  } | null>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);
  const canConfigureColumns = Boolean(
    resetPreferences && toggleColumnVisibility,
  );

  useEffect(() => {
    onConfigurationOpenChange?.(headerMenu !== null);
  }, [headerMenu, onConfigurationOpenChange]);

  useEffect(() => {
    if (!headerMenu) {
      return;
    }

    function closeMenu(event: PointerEvent) {
      if (!headerMenuRef.current?.contains(event.target as Node)) {
        setHeaderMenu(null);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setHeaderMenu(null);
      }
    }

    document.addEventListener("pointerdown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [headerMenu]);

  if (!items.length) {
    return <>{emptyState}</>;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <div className="relative min-h-0 min-w-0 flex-1">
        <div className={scrollClassName} {...scrollAttribute}>
          <table
            className={tableClassName}
            style={
              fillWidth
                ? { minWidth: Math.max(tableWidth, 1), width: "100%" }
                : { width: Math.max(tableWidth, 1) }
            }
          >
            <colgroup>
              {leadingExtraColumns.map((column) => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
              {orderedColumns.map((column) => (
                <col
                  key={column.id}
                  style={{ width: columnWidths[column.id] }}
                />
              ))}
              {trailingExtraColumns.map((column) => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead
              className={headerClassName}
              onContextMenu={
                canConfigureColumns
                  ? (event) => {
                      event.preventDefault();
                      const position = fitContextMenuToViewport(
                        event.clientX,
                        event.clientY,
                        288,
                        112,
                      );
                      setHeaderMenu({ mode: "actions", ...position });
                    }
                  : undefined
              }
            >
              <tr>
                {leadingExtraColumns.map((column) => (
                  <th
                    className={
                      column.headerClassName ??
                      "border-b border-slate-100 px-3 py-2 font-semibold dark:border-slate-800"
                    }
                    key={column.key}
                  >
                    {column.header}
                  </th>
                ))}
                {orderedColumns.map((column) => (
                  <ConfigurableDataTableHeader
                    column={column}
                    key={column.id}
                    moveColumn={moveColumn}
                    resizeColumn={resizeColumn}
                    sort={sort}
                    toggleSort={toggleSort}
                    width={columnWidths[column.id]}
                  />
                ))}
                {trailingExtraColumns.map((column) => (
                  <th
                    className={
                      column.headerClassName ??
                      "border-b border-slate-100 px-3 py-2 font-semibold dark:border-slate-800"
                    }
                    key={column.key}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sortedItems.map((item) => (
                <tr
                  className={rowClassName?.(item)}
                  key={getRowKey(item)}
                  onClick={onRowClick ? () => void onRowClick(item) : undefined}
                  onContextMenu={
                    onRowContextMenu
                      ? (event) => onRowContextMenu(item, event)
                      : undefined
                  }
                  onDoubleClick={
                    onRowDoubleClick
                      ? (event) => void onRowDoubleClick(item, event)
                      : undefined
                  }
                  onKeyDown={
                    onRowKeyDown
                      ? (event) => onRowKeyDown(item, event)
                      : undefined
                  }
                  onPointerDown={
                    onRowPointerDown
                      ? (event) => onRowPointerDown(item, event)
                      : undefined
                  }
                  onPointerEnter={
                    onRowPointerEnter
                      ? (event) => onRowPointerEnter(item, event)
                      : undefined
                  }
                  title={rowTitle?.(item)}
                  tabIndex={
                    typeof rowTabIndex === "function"
                      ? rowTabIndex(item)
                      : rowTabIndex
                  }
                >
                  {leadingExtraColumns.map((column) => (
                    <td
                      className={
                        column.cellClassName ??
                        "px-3 py-3 align-top text-slate-700 dark:text-slate-300"
                      }
                      key={column.key}
                    >
                      {column.render(item)}
                    </td>
                  ))}
                  {orderedColumns.map((column) => {
                    const children = column.render(item);

                    return (
                      <td className={cellClassName} key={column.id}>
                        <div className={cellContentClassName}>
                          {renderCellContent
                            ? renderCellContent({ children, column, item })
                            : children}
                        </div>
                      </td>
                    );
                  })}
                  {trailingExtraColumns.map((column) => (
                    <td
                      className={
                        column.cellClassName ??
                        "px-3 py-3 align-top text-slate-700 dark:text-slate-300"
                      }
                      key={column.key}
                    >
                      {column.render(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {headerMenu && toggleColumnVisibility && resetPreferences ? (
        <div
          className="fixed z-[80]"
          onContextMenu={(event) => event.preventDefault()}
          ref={headerMenuRef}
          style={{ left: headerMenu.x, top: headerMenu.y }}
        >
          {headerMenu.mode === "actions" ? (
            <div
              className="grid w-56 gap-1 rounded-md border border-slate-200 bg-white p-1.5 text-sm shadow-xl dark:border-slate-800 dark:bg-neutral-950"
              role="menu"
            >
              <button
                className="flex items-center gap-2 rounded px-2.5 py-2 text-left text-slate-700 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:text-slate-200 dark:hover:bg-slate-900"
                onClick={() => {
                  const position = fitContextMenuToViewport(
                    headerMenu.x,
                    headerMenu.y,
                    320,
                    480,
                  );
                  setHeaderMenu({ mode: "columns", ...position });
                }}
                role="menuitem"
                type="button"
              >
                <Columns3 className="h-4 w-4" />
                Editar lista
              </button>
              <button
                className="flex items-center gap-2 rounded px-2.5 py-2 text-left text-slate-700 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:text-slate-200 dark:hover:bg-slate-900"
                onClick={() => {
                  if (onResetPreferencesRequest) {
                    onResetPreferencesRequest();
                  } else {
                    resetPreferences();
                  }
                  setHeaderMenu(null);
                }}
                role="menuitem"
                type="button"
              >
                <RotateCcw className="h-4 w-4" />
                {resetMenuLabel}
              </button>
            </div>
          ) : (
            <ConfigurableDataTableColumnEditor
              className="w-80 rounded-md border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-800 dark:bg-neutral-950"
              columns={columns}
              columnGroups={columnGroups}
              showReset={false}
              toggleColumnVisibility={toggleColumnVisibility}
              visibleColumns={visibleColumns}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

function fitContextMenuToViewport(
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const margin = 8;

  return {
    x: Math.max(margin, Math.min(x, window.innerWidth - width - margin)),
    y: Math.max(margin, Math.min(y, window.innerHeight - height - margin)),
  };
}

export function ConfigurableDataTableColumnEditor<
  TItem,
  TColumn extends string,
>({
  className = "absolute right-0 top-10 z-40 w-80 rounded-md border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-800 dark:bg-neutral-950",
  columns,
  columnGroups,
  confirmResetTable,
  description = "Arraste os cabeçalhos para reorganizar a ordem.",
  onCancelReset,
  onConfirmReset,
  onRequestReset,
  showReset = true,
  toggleColumnVisibility,
  visibleColumns,
}: {
  className?: string;
  columns: Array<ConfigurableDataTableColumn<TItem, TColumn>>;
  columnGroups?: Array<ConfigurableDataTableColumnGroup<TColumn>>;
  confirmResetTable?: boolean;
  description?: string;
  onCancelReset?: () => void;
  onConfirmReset?: () => void;
  onRequestReset?: () => void;
  showReset?: boolean;
  toggleColumnVisibility: (column: TColumn) => void;
  visibleColumns: TColumn[];
}) {
  const groups = columnGroups?.length
    ? columnGroups
    : [
        {
          columns: columns.map((column) => column.id),
          id: "all",
          label: "Colunas",
        },
      ];

  return (
    <div className={className}>
      <div>
        <p className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
          Colunas visíveis
        </p>
        {description ? (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>

      <div className="mt-3 max-h-80 space-y-3 overflow-y-auto pr-1">
        {groups.map((group) => (
          <section key={group.id}>
            <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {group.label}
            </p>
            <div className="grid gap-1">
              {group.columns.map((columnId) => {
                const column = columns.find((item) => item.id === columnId);

                if (!column) {
                  return null;
                }

                return (
                  <label
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
                    key={column.id}
                  >
                    <input
                      checked={visibleColumns.includes(column.id)}
                      className="h-4 w-4 rounded border-slate-300"
                      disabled={
                        visibleColumns.length === 1 &&
                        visibleColumns.includes(column.id)
                      }
                      onChange={() => toggleColumnVisibility(column.id)}
                      type="checkbox"
                    />
                    {column.label}
                  </label>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {showReset ? (
        <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-800">
          {confirmResetTable ? (
            <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-900 ring-1 ring-amber-100 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-900/60">
              <p className="font-medium">Restaurar padrão da tabela?</p>
              <div className="mt-2 flex justify-end gap-2">
                <button
                  className="rounded px-2 py-1 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/50"
                  onClick={onCancelReset}
                  type="button"
                >
                  Cancelar
                </button>
                <button
                  className="rounded bg-amber-600 px-2 py-1 font-medium text-white hover:bg-amber-700"
                  onClick={onConfirmReset}
                  type="button"
                >
                  Restaurar
                </button>
              </div>
            </div>
          ) : (
            <button
              className="text-xs font-medium text-slate-500 transition hover:text-slate-950 dark:text-slate-400 dark:hover:text-white"
              onClick={onRequestReset}
              type="button"
            >
              Restaurar tabela
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function sortConfigurableDataTableItems<TItem, TColumn extends string>(
  items: TItem[],
  sort: TableSortState<TColumn>,
  columns: Array<ConfigurableDataTableColumn<TItem, TColumn>>,
) {
  if (!sort) {
    return items;
  }

  const column = columns.find((item) => item.id === sort.column);

  if (!column) {
    return items;
  }

  return [...items].sort((first, second) => {
    const result = compareDataTableValues(
      column.sortValue(first),
      column.sortValue(second),
    );

    return sort.direction === "asc" ? result : -result;
  });
}

export function dataTableDateSortValue(value?: string | null) {
  if (!value) {
    return 0;
  }

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? 0 : time;
}

function ConfigurableDataTableHeader<TItem, TColumn extends string>({
  column,
  moveColumn,
  resizeColumn,
  sort,
  toggleSort,
  width,
}: {
  column: ConfigurableDataTableColumn<TItem, TColumn>;
  moveColumn: (source: TColumn, target: TColumn) => void;
  resizeColumn: (column: TColumn, width: number) => void;
  sort: TableSortState<TColumn>;
  toggleSort: (column: TColumn) => void;
  width: number;
}) {
  const isSorted = sort?.column === column.id;
  const isSortable = column.sortable !== false;
  const SortIcon = !isSorted
    ? ArrowUpDown
    : sort.direction === "asc"
      ? ArrowUp
      : ArrowDown;

  function startResize(event: ReactMouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = width;
    let nextWidth = startWidth;
    const tableScroll = event.currentTarget.closest(
      "[data-configurable-table-scroll]",
    ) as HTMLElement | null;
    const tableRect = tableScroll?.getBoundingClientRect();
    const previewLine = document.createElement("div");
    const getPreviewLeft = (targetWidth: number) =>
      tableScroll && tableRect
        ? tableScroll.scrollLeft +
          startX +
          targetWidth -
          startWidth -
          tableRect.left
        : startX + targetWidth - startWidth;

    previewLine.style.position = "absolute";
    previewLine.style.top = "0";
    previewLine.style.bottom = "0";
    previewLine.style.left = `${getPreviewLeft(startWidth)}px`;
    previewLine.style.zIndex = "30";
    previewLine.style.width = "2px";
    previewLine.style.pointerEvents = "none";
    previewLine.style.background = "rgb(148 163 184)";
    previewLine.style.opacity = "0.9";
    (tableScroll ?? document.body).appendChild(previewLine);

    function handleMove(moveEvent: MouseEvent) {
      nextWidth = Math.max(
        column.minWidth,
        startWidth + moveEvent.clientX - startX,
      );
      previewLine.style.left = `${getPreviewLeft(nextWidth)}px`;
    }

    function handleUp() {
      resizeColumn(column.id, nextWidth);
      previewLine.remove();
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    }

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }

  return (
    <th
      className="group relative border-b border-slate-100 px-3 py-2 font-semibold dark:border-slate-800"
      draggable
      onDragOver={(event) => event.preventDefault()}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", column.id);
      }}
      onDrop={(event) => {
        event.preventDefault();
        const source = event.dataTransfer.getData("text/plain") as TColumn;
        moveColumn(source, column.id);
      }}
      style={{ width }}
    >
      <button
        className={`flex w-full items-center justify-between gap-2 text-left ${
          isSortable ? "" : "cursor-default"
        }`}
        onClick={isSortable ? () => toggleSort(column.id) : undefined}
        type="button"
      >
        <span className="truncate">{column.label}</span>
        {isSortable ? (
          <SortIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        ) : null}
      </button>
      <button
        aria-label={`Redimensionar coluna ${column.label}`}
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize border-r border-transparent transition hover:border-slate-400 group-hover:border-slate-300"
        onMouseDown={startResize}
        type="button"
      />
    </th>
  );
}

function compareDataTableValues(
  first: string | number,
  second: string | number,
) {
  if (typeof first === "number" && typeof second === "number") {
    return first - second;
  }

  return String(first).localeCompare(String(second), "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
}
